import React, { useEffect, useState } from 'react';
import { freightAPI, opsAPI, trucksAPI } from '../../../services/api';
import Table from '../../Common/Table';
import Modal from '../../Common/Modal';

const TABS = [
  { id: 'tracking', label: 'Tracking' },
  { id: 'late', label: 'Late PU/DEL' },
  { id: 'issues', label: 'Freight issues' },
  { id: 'breakdowns', label: 'Breakdowns' },
  { id: 'documents', label: 'BOL / POD' },
  { id: 'invoices', label: 'Invoices' },
  { id: 'rate', label: 'Rate / P&L' },
  { id: 'reports', label: 'Reports' },
];

function money(v) {
  if (v == null || v === '') return '—';
  return `$${Number(v).toFixed(2)}`;
}

export default function OpsConsole() {
  const [tab, setTab] = useState('tracking');
  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Operations</h1>
      </div>
      <div className="form-row" style={{ flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={tab === t.id ? 'btn-primary' : 'btn-secondary'}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'tracking' && <TrackingTab />}
      {tab === 'late' && <LateTab />}
      {tab === 'issues' && <IssuesTab />}
      {tab === 'breakdowns' && <BreakdownsTab />}
      {tab === 'documents' && <DocumentsTab />}
      {tab === 'invoices' && <InvoicesTab />}
      {tab === 'rate' && <RateTab />}
      {tab === 'reports' && <ReportsTab />}
    </div>
  );
}

function TrackingTab() {
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ ping_interval_seconds: 120, avg_speed_mph: 50 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const [latest, s] = await Promise.all([opsAPI.getTrackingLatest(), opsAPI.getSettings()]);
      setRows(Array.isArray(latest) ? latest : []);
      setSettings(s);
      setForm({
        ping_interval_seconds: s.ping_interval_seconds,
        avg_speed_mph: s.avg_speed_mph,
        cpm_base: s.cpm_base,
        cpm_per_1000lbs: s.cpm_per_1000lbs,
      });
    } catch (e) {
      alert(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      const s = await opsAPI.updateSettings(form);
      setSettings(s);
      alert('Settings saved');
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <>
      <form onSubmit={saveSettings} className="form-row" style={{ gap: 12, marginBottom: 20, alignItems: 'flex-end' }}>
        <div className="form-group">
          <label>Ping interval (seconds)</label>
          <input type="number" min="15" max="3600" value={form.ping_interval_seconds}
            onChange={(e) => setForm({ ...form, ping_interval_seconds: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Avg speed (mph)</label>
          <input type="number" step="0.1" value={form.avg_speed_mph}
            onChange={(e) => setForm({ ...form, avg_speed_mph: e.target.value })} />
        </div>
        <div className="form-group">
          <label>CPM base ($/mi @ 0 lb)</label>
          <input type="number" step="0.01" value={form.cpm_base || ''}
            onChange={(e) => setForm({ ...form, cpm_base: e.target.value })} />
        </div>
        <div className="form-group">
          <label>CPM per 1,000 lb</label>
          <input type="number" step="0.01" value={form.cpm_per_1000lbs || ''}
            onChange={(e) => setForm({ ...form, cpm_per_1000lbs: e.target.value })} />
        </div>
        <button type="submit" className="btn-primary">Save settings</button>
      </form>
      {settings && (
        <p style={{ marginBottom: 12, color: 'var(--text-medium)' }}>
          Running ETA uses last GPS ping and {settings.avg_speed_mph} mph (straight-line).
        </p>
      )}
      {loading ? <div className="loading">Loading...</div> : (
        <Table
          data={rows.map((r) => ({
            ...r,
            name: `${r.first_name || ''} ${r.last_name || ''}`.trim(),
            coords: `${r.latitude}, ${r.longitude}`,
          }))}
          columns={[
            { key: 'name', label: 'Driver' },
            { key: 'truck_number', label: 'Truck' },
            { key: 'freight_number', label: 'Load' },
            { key: 'coords', label: 'Last ping' },
            { key: 'recorded_at', label: 'Time' },
          ]}
        />
      )}
    </>
  );
}

function LateTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    opsAPI.getStopEvents().then((d) => {
      const all = Array.isArray(d) ? d : [];
      setRows(all.filter((r) => r.event_type === 'late_pickup' || r.event_type === 'late_delivery'));
    }).catch((e) => alert(e.message));
  }, []);
  return (
    <Table
      data={rows.map((r) => ({
        ...r,
        driver: `${r.driver_first_name || ''} ${r.driver_last_name || ''}`.trim(),
      }))}
      columns={[
        { key: 'created_at', label: 'When' },
        { key: 'event_type', label: 'Type' },
        { key: 'freight_number', label: 'Load' },
        { key: 'driver', label: 'Driver' },
        { key: 'minutes_late', label: 'Min late' },
        { key: 'explanation', label: 'Explanation' },
      ]}
    />
  );
}

function IssuesTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    opsAPI.getExceptions().then((d) => setRows(Array.isArray(d) ? d : [])).catch((e) => alert(e.message));
  }, []);
  return (
    <Table
      data={rows.map((r) => ({
        ...r,
        driver: `${r.driver_first_name || ''} ${r.driver_last_name || ''}`.trim(),
      }))}
      columns={[
        { key: 'created_at', label: 'When' },
        { key: 'freight_number', label: 'Load' },
        { key: 'reason_label', label: 'Reason', format: (v, r) => v || r.reason_code },
        { key: 'driver', label: 'Driver' },
        { key: 'notes', label: 'Notes' },
      ]}
    />
  );
}

function BreakdownsTab() {
  const [rows, setRows] = useState([]);
  const load = () =>
    opsAPI.getBreakdowns().then((d) => setRows(Array.isArray(d) ? d : [])).catch((e) => alert(e.message));
  useEffect(() => { load(); }, []);

  const resolve = async (row) => {
    try {
      await opsAPI.resolveBreakdown(row.id);
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <Table
      data={rows.map((r) => ({
        ...r,
        driver: `${r.driver_first_name || ''} ${r.driver_last_name || ''}`.trim(),
      }))}
      columns={[
        { key: 'started_at', label: 'Started' },
        { key: 'status', label: 'Status' },
        { key: 'truck_number', label: 'Truck' },
        { key: 'driver', label: 'Driver' },
        { key: 'location_text', label: 'Location' },
        { key: 'notes', label: 'Notes' },
        { key: 'alerted_at', label: 'Alerted' },
      ]}
      renderActions={(row) =>
        row.status === 'open' ? (
          <button type="button" className="btn-edit" onClick={() => resolve(row)}>
            Resolve
          </button>
        ) : null
      }
    />
  );
}

function DocumentsTab() {
  const [rows, setRows] = useState([]);
  const [doc, setDoc] = useState(null);
  const [to, setTo] = useState('');
  const load = () =>
    opsAPI.getDocuments().then((d) => setRows(Array.isArray(d) ? d : [])).catch((e) => alert(e.message));
  useEffect(() => { load(); }, []);

  const open = async (row) => {
    try {
      const full = await opsAPI.getDocument(row.id);
      setDoc(full);
      setTo(full.recipient_email || full.emailed_to || '');
    } catch (e) {
      alert(e.message);
    }
  };

  const send = async () => {
    try {
      await opsAPI.emailDocument(doc.id, to);
      setDoc(null);
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <>
      <Table
        data={rows}
        columns={[
          { key: 'created_at', label: 'Created' },
          { key: 'doc_type', label: 'Type' },
          { key: 'freight_number', label: 'Load' },
          { key: 'has_signature', label: 'Signature', format: (v) => (v ? 'Yes' : '—') },
          { key: 'has_photo', label: 'Photo', format: (v) => (v ? 'Yes' : '—') },
          { key: 'emailed_at', label: 'Emailed' },
        ]}
        renderActions={(row) => (
          <button type="button" className="btn-edit" onClick={() => open(row)}>
            Open
          </button>
        )}
      />
      <Modal show={!!doc} onClose={() => setDoc(null)} title={doc ? `${doc.doc_type} ${doc.freight_number}` : ''}>
        {doc && (
          <>
            <div className="form-group">
              <label>Email to broker / shipper</label>
              <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="ap@broker.com" />
            </div>
            {doc.signature_png_base64 && (
              <div className="form-group">
                <label>Signature</label>
                <img alt="signature" style={{ maxWidth: '100%', background: '#fff' }}
                  src={doc.signature_png_base64.startsWith('data:') ? doc.signature_png_base64 : `data:image/png;base64,${doc.signature_png_base64}`} />
              </div>
            )}
            {doc.photo_jpeg_base64 && (
              <div className="form-group">
                <label>Photo</label>
                <img alt="pod" style={{ maxWidth: '100%' }}
                  src={doc.photo_jpeg_base64.startsWith('data:') ? doc.photo_jpeg_base64 : `data:image/jpeg;base64,${doc.photo_jpeg_base64}`} />
              </div>
            )}
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => setDoc(null)}>Close</button>
              <button type="button" className="btn-primary" onClick={send}>Email</button>
            </div>
          </>
        )}
      </Modal>
    </>
  );
}

function InvoicesTab() {
  const [rows, setRows] = useState([]);
  const [freight, setFreight] = useState([]);
  const [form, setForm] = useState({ freight_id: '', source: 'rate_con', amount: '', bill_to_name: '', bill_to_email: '' });
  const load = () =>
    opsAPI.getInvoices().then((d) => setRows(Array.isArray(d) ? d : [])).catch((e) => alert(e.message));
  useEffect(() => {
    load();
    freightAPI.getAll().then(setFreight).catch(() => setFreight([]));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const body = {
        freight_id: Number(form.freight_id),
        source: form.source,
        bill_to_name: form.bill_to_name || undefined,
        bill_to_email: form.bill_to_email || undefined,
      };
      if (form.source === 'manual') body.amount = Number(form.amount);
      await opsAPI.createInvoice(body);
      setForm({ ...form, amount: '', bill_to_name: '', bill_to_email: '' });
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <>
      <form onSubmit={create} style={{ marginBottom: 20 }}>
        <div className="form-row">
          <div className="form-group">
            <label>Load</label>
            <select required value={form.freight_id} onChange={(e) => setForm({ ...form, freight_id: e.target.value })}>
              <option value="">Select freight</option>
              {freight.map((f) => (
                <option key={f.id} value={f.id}>{f.freight_number}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Source</label>
            <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
              <option value="rate_con">From rate confirmation</option>
              <option value="manual">Manual</option>
            </select>
          </div>
          {form.source === 'manual' && (
            <div className="form-group">
              <label>Amount</label>
              <input type="number" step="0.01" required value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>
          )}
          <div className="form-group">
            <label>Bill to name</label>
            <input value={form.bill_to_name} onChange={(e) => setForm({ ...form, bill_to_name: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Bill to email</label>
            <input type="email" value={form.bill_to_email} onChange={(e) => setForm({ ...form, bill_to_email: e.target.value })} />
          </div>
        </div>
        <button type="submit" className="btn-primary">Create invoice</button>
      </form>
      <Table
        data={rows}
        columns={[
          { key: 'created_at', label: 'Created' },
          { key: 'freight_number', label: 'Load' },
          { key: 'source', label: 'Source' },
          { key: 'amount', label: 'Amount', format: money },
          { key: 'bill_to_name', label: 'Bill to' },
          { key: 'status', label: 'Status' },
        ]}
      />
    </>
  );
}

function RateTab() {
  const [freight, setFreight] = useState([]);
  const [id, setId] = useState('');
  const [bundle, setBundle] = useState(null);
  const [form, setForm] = useState({});

  useEffect(() => {
    freightAPI.getAll().then(setFreight).catch(() => setFreight([]));
  }, []);

  const load = async (freightId) => {
    try {
      const data = await opsAPI.getRate(freightId);
      setBundle(data);
      setForm(data.rate || {});
    } catch (e) {
      alert(e.message);
    }
  };

  const lineHaulProduct =
    Number(form.line_haul_miles || 0) * Number(form.line_haul_rate_per_mile || 0);

  const save = async (e) => {
    e.preventDefault();
    try {
      const data = await opsAPI.saveRate(id, form);
      setBundle(data);
      setForm(data.rate || {});
    } catch (err) {
      alert(err.message);
    }
  };

  const set = (k, v) => setForm({ ...form, [k]: v === '' ? null : v });

  return (
    <>
      <div className="form-group" style={{ maxWidth: 360, marginBottom: 16 }}>
        <label>Load</label>
        <select value={id} onChange={(e) => { setId(e.target.value); if (e.target.value) load(e.target.value); }}>
          <option value="">Select freight</option>
          {freight.map((f) => (
            <option key={f.id} value={f.id}>{f.freight_number}</option>
          ))}
        </select>
      </div>
      {bundle && (
        <form onSubmit={save}>
          <div className="form-row">
            <div className="form-group"><label>DH miles</label><input type="number" step="0.1" value={form.dh_miles || ''} onChange={(e) => set('dh_miles', e.target.value)} /></div>
            <div className="form-group"><label>Loaded miles</label><input type="number" step="0.1" value={form.loaded_miles || ''} onChange={(e) => set('loaded_miles', e.target.value)} /></div>
            <div className="form-group"><label>Weight (lbs)</label><input type="number" step="1" value={form.weight_lbs || ''} onChange={(e) => set('weight_lbs', e.target.value)} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Line-haul miles</label><input type="number" step="0.1" value={form.line_haul_miles || ''} onChange={(e) => set('line_haul_miles', e.target.value)} /></div>
            <div className="form-group"><label>Rate / mile</label><input type="number" step="0.01" value={form.line_haul_rate_per_mile || ''} onChange={(e) => set('line_haul_rate_per_mile', e.target.value)} /></div>
            <div className="form-group"><label>Product</label><input readOnly value={lineHaulProduct ? lineHaulProduct.toFixed(2) : ''} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Line-haul amount (override)</label><input type="number" step="0.01" value={form.line_haul_amount || ''} onChange={(e) => set('line_haul_amount', e.target.value)} /></div>
            <div className="form-group"><label>Labor cost</label><input type="number" step="0.01" value={form.labor_cost || ''} onChange={(e) => set('labor_cost', e.target.value)} /></div>
            <div className="form-group"><label>Insurance (optional)</label><input type="number" step="0.01" value={form.insurance_cost || ''} onChange={(e) => set('insurance_cost', e.target.value)} /></div>
            <div className="form-group"><label>Truck cost (optional)</label><input type="number" step="0.01" value={form.truck_cost || ''} onChange={(e) => set('truck_cost', e.target.value)} /></div>
          </div>
          <p>
            Subtotal {money(bundle.pnl?.subtotal)} · Line haul {money(bundle.pnl?.line_haul)} · Profit{' '}
            <strong>{money(bundle.pnl?.profit)}</strong>
          </p>
          <button type="submit" className="btn-primary">Save P&amp;L</button>
        </form>
      )}
    </>
  );
}

function ReportsTab() {
  const [trucks, setTrucks] = useState([]);
  const [period, setPeriod] = useState('month');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [truckId, setTruckId] = useState('');
  const [freightNumber, setFreightNumber] = useState('');
  const [report, setReport] = useState(null);

  useEffect(() => {
    trucksAPI.getAll().then(setTrucks).catch(() => setTrucks([]));
  }, []);

  const run = async (e) => {
    e.preventDefault();
    try {
      const params = { period };
      if (period === 'custom') {
        params.from = from;
        params.to = to;
      }
      if (truckId) params.truck_id = truckId;
      if (freightNumber) params.freight_number = freightNumber;
      setReport(await opsAPI.getReports(params));
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <>
      <form onSubmit={run} className="form-row" style={{ gap: 12, alignItems: 'flex-end', marginBottom: 16 }}>
        <div className="form-group">
          <label>Period</label>
          <select value={period} onChange={(e) => setPeriod(e.target.value)}>
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
            <option value="quarter">Quarter</option>
            <option value="semi_annual">Semi-annual</option>
            <option value="annual">Annual</option>
            <option value="custom">Custom calendar</option>
          </select>
        </div>
        {period === 'custom' && (
          <>
            <div className="form-group"><label>From</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} required /></div>
            <div className="form-group"><label>To</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} required /></div>
          </>
        )}
        <div className="form-group">
          <label>Truck</label>
          <select value={truckId} onChange={(e) => setTruckId(e.target.value)}>
            <option value="">All trucks</option>
            {trucks.map((t) => (
              <option key={t.id} value={t.id}>{t.truck_number}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Load number</label>
          <input value={freightNumber} onChange={(e) => setFreightNumber(e.target.value)} />
        </div>
        <button type="submit" className="btn-primary">Run</button>
      </form>
      {report && (
        <>
          <p>
            {report.load_count} loads · Revenue {money(report.revenue)} · Profit {money(report.profit)} · DH {report.dh_miles} · Loaded {report.loaded_miles}
          </p>
          <p>Breakdowns: {report.breakdowns?.total || 0} · Days OOS {report.breakdowns?.days_oos || 0}</p>
          <p>Miles by state (GPS approx): {Object.entries(report.miles_by_state || {}).map(([k, v]) => `${k} ${v}`).join(', ') || '—'}</p>
          <Table
            data={report.loads || []}
            columns={[
              { key: 'freight_number', label: 'Load' },
              { key: 'truck_id', label: 'Truck ID' },
              { key: 'revenue', label: 'Revenue', format: money },
              { key: 'profit', label: 'Profit', format: money },
            ]}
          />
          <h3 style={{ margin: '16px 0 8px' }}>Days out of service by truck</h3>
          <Table
            data={report.breakdowns?.by_truck || []}
            columns={[
              { key: 'truck_number', label: 'Truck' },
              { key: 'breakdown_count', label: 'Breakdowns' },
              { key: 'days_oos', label: 'Days OOS' },
            ]}
          />
        </>
      )}
    </>
  );
}
